import Input from "@/components/input"
import "./index.scss"
import PasswordInput from "@/components/password-input"
import Button from "@/components/button"
import { TinyText } from "@/components/tiny-text"
import { login } from "@/services/auth"
import { useNavigate } from "react-router"
import { FormEvent, useState } from "react"
import useStore from "@/store"
import { HTTPError } from "ky"
import { toast } from "sonner"
import { EnumsOf } from "@/lib/utils"
import { handleNetworkError } from "@/services/base"
import { usePostHog } from "posthog-js/react"
import { UserSlice } from "@/store/user"
import { useFieldError, useFieldMeta, useForm } from "@/lib/form/form"
import * as y from "yup"
import { getShiftStatus, setSession } from "@/services/session"
import { ShiftStatus } from "@/store/session"
import { FormParamStatus } from "@/lib/form/types"

interface LoginForm {
  code: string
  password: string
}

const validator = y.object({
  code: y.string().trim().required(),
  password: y.string().trim().required(),
})

function useLoginForm() {
  const form = useForm({
    validator: validator,
  })

  return form
}

const PageState = {
  Ready: "Ready",
  Success: "Success",
  Loading: "Loading",
} as const
type PageState = EnumsOf<typeof PageState>

export default function LoginPage() {
  const navigate = useNavigate()
  const form = useLoginForm()
  const { setValue, getValues, runAllValidators, getFieldStatus } = form
  const store = useStore()
  const appStore = useStore.use.app()
  const sessionStore = useStore.use.session()
  const setUserState = useStore.use.setUserState()
  const [state, setState] = useState<PageState>(PageState.Ready)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const posthog = usePostHog()
  const [codeError, passwordError] = useFieldError({
    form,
    names: ["code", "password"],
  })
  const codeMeta = useFieldMeta({
    form,
    name: "code",
  })
  const passwordMeta = useFieldMeta({
    form,
    name: "password",
  })

  function handleChange(field: string, value: string) {
    setValue(field, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  function handleBlur(field: string, value: string) {
    setValue(field, value, {
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  async function handleLoginSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    e.stopPropagation()
    setSubmitAttempted(true)

    await runAllValidators()

    if (getFieldStatus() !== FormParamStatus.VALID) {
      return
    }

    await onSubmit(getValues() as LoginForm)
  }

  async function onSubmit(data: LoginForm) {
    try {
      setState(PageState.Loading)

      const res = await login({
        employeeCode: data.code,
        password: data.password,
      })

      const newState: UserSlice["user"] = {
        employee: {
          code: data.code,
          ...res.employee,
        },
        plant: {
          id: res.plant.plantId,
          name: res.plant.name,
          city: res.plant.city,
        },
        shifts: res.shifts.map((item) => ({
          id: item.shiftId,
          name: item.name,
          startTime: item.startTime,
          endTime: item.endTime,
        })),
        processes: res.processes.map((item) => ({
          id: item.processId,
          name: item.name,
          type: item.type,
        })),
        token: res.token,
      }
      setUserState(newState)

      setState(PageState.Success)

      const status = await getShiftStatus()
      if (status.shiftSession?.status === ShiftStatus.Started) {
        const session = status.shiftSession

        sessionStore.setShiftDetails({
          id: session.shiftId,
          startedAt: session.shiftStartedAt,
          endedAt: session.shiftEndedAt || "",
          name: session.shiftName,
        })
        
        setSession(session)

        await navigate("/manage-processes")
      } else {
        setSession(null)
        await navigate("/onboard-shift")
      }
    } catch (err) {
      store.logout()
      // TODO: Remove sending plaintext passwords
      posthog.captureException(err, {
        data: data,
      })
      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error((err as any)?.name || "Error logging in")
      console.error(err)
    } finally {
      setState(PageState.Ready)
    }
  }

  return (
    <div className="login-page wrapper flex h-lvh flex-col justify-center">
      <div className="bg-backdrop h-[90%] rounded-lg">
        <div className="flex flex-col items-center mt-8">
          <img
            className="w-30"
            src="/assets/haldiram-logo.png"
            alt="Haldiram's Logo"
          />

          <h2 className="text-base font-semibold">Unit Quality</h2>
        </div>

        <form className="mt-8 px-4" onSubmit={handleLoginSubmit}>
          <h1 className="text-2xl font-semibold">Log in</h1>

          <div className="mt-5 flex flex-col gap-4">
            <div>
              <Input
                label="Employee code"
                name="username"
                placeholder="Enter your employee code"
                onChange={(value) => handleChange("code", value)}
                onBlur={(value) => handleBlur("code", value)}
              />
              {(submitAttempted || codeMeta.touched) && codeError && (
                <TinyText
                  error
                  text="Employee code is required"
                  className="text-sm leading-4"
                />
              )}
            </div>

            <div>
              <PasswordInput
                label="Password"
                name="password"
                placeholder="Enter your password"
                onChange={(value) => handleChange("password", value)}
                onBlur={(value) => handleBlur("password", value)}
              />
              {(submitAttempted || passwordMeta.touched) && passwordError && (
                <TinyText
                  error
                  text="Password is required"
                  className="text-sm leading-4"
                />
              )}
            </div>
          </div>

          <Button
            variant="default"
            className="mt-10 w-full"
            type="submit"
            disabled={state === PageState.Loading}
            isLoading={state === PageState.Loading}
          >
            Login
          </Button>

          <img
            className="mx-auto mt-14.5"
            alt="Q-Darpan logo"
            src="/assets/darpan-logo.png"
          />

          <p className="text-label-text text-xs mt-5 text-center">
            {appStore.version}
          </p>
        </form>
      </div>
    </div>
  )
}
